import { Feature } from '@reliance/types/enums/Feature';
import { Configuration } from '@reliance/types/models/Configuration';
import { Facility } from '@reliance/types/models/Facility';
import React, { useMemo, useState } from 'react';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import FormGroup from 'react-bootstrap/FormGroup';
import { Field, FormRenderProps } from 'react-final-form';
import FeatureConfigurationModal from './FeatureConfigurationModal';
import Checkbox from '../form/Checkbox';
import { InputType } from '../form/TextInput';
import { saveFacilityConfigurations, useFacilityConfigurations } from '../hooks/Configuration';
import { getFacilityFeatureList } from './facilityFeatures';

export type FeatureConfigurationOption = {
  value: string;
  label: string;
};

export type FeatureConfigurationKey = {
  key: string;
  label: string;
  type?: InputType;
  defaultValue?: string;
  options?: FeatureConfigurationOption[];
};

export type FeatureActivationRequirement = {
  featureValue: string;
  configurationKeys: FeatureConfigurationKey[];
  modalTitle: string;
};

const BOOKING_CALL_MINUTES_OPTIONS: FeatureConfigurationOption[] = [
  { value: '5', label: '5 minutes' },
  { value: '10', label: '10 minutes' },
];

// Facility fields used when building feature activation requirements
export type FeatureActivationFacility = Pick<Facility, 'state'>;

function isMinnesotaFacility(facility?: FeatureActivationFacility | null): boolean {
  const normalized = (facility?.state ?? '').trim().toUpperCase();
  return normalized === 'MN' || normalized === 'MINNESOTA';
}

// Default the booking call minutes to 10 for Minnesota, otherwise 5
export function bookingCallMinutesDefault(facility?: FeatureActivationFacility | null): string {
  return isMinnesotaFacility(facility) ? '10' : '5';
}

const STATIC_FEATURE_ACTIVATION_REQUIREMENTS: Partial<Record<string, FeatureActivationRequirement>> = {
  // Edovo: Requires the Edovo subdirectory to be configured on the facility
  [Feature.EDOVO]: {
    featureValue: Feature.EDOVO,
    configurationKeys: [{ key: 'EdovoSubdirectory', label: 'Edovo subdirectory' }],
    modalTitle: 'Edovo configuration',
  },

  // CorrecTek: Requires per-facility SFTP server and credentials
  [Feature.CORRECTEK]: {
    featureValue: Feature.CORRECTEK,
    configurationKeys: [
      { key: 'CorrecTekSftpServer', label: 'CorrecTek SFTP server' },
      { key: 'CorrecTekSftpUsername', label: 'CorrecTek SFTP username' },
      { key: 'CorrecTekSftpPassword', label: 'CorrecTek SFTP password' },
    ],
    modalTitle: 'CorrecTek configuration',
  },

  // All calling: Requires "SiteName"
  [Feature.WIRELESS_AUDIO_CALLS]: {
    featureValue: Feature.WIRELESS_AUDIO_CALLS,
    configurationKeys: [{ key: 'SiteName', label: 'Site Name' }],
    modalTitle: 'Wireless Audio Calling configuration',
  },
  [Feature.WIRELESS_VIDEO_CALLS]: {
    featureValue: Feature.WIRELESS_VIDEO_CALLS,
    configurationKeys: [{ key: 'SiteName', label: 'Site Name' }],
    modalTitle: 'Wireless Video Calling configuration',
  },
  [Feature.INBOUND_AUDIO_CALLS]: {
    featureValue: Feature.INBOUND_AUDIO_CALLS,
    configurationKeys: [{ key: 'SiteName', label: 'Site Name' }],
    modalTitle: 'Inbound Audio Calling configuration',
  },
  [Feature.VIDEO_CALLS]: {
    featureValue: Feature.VIDEO_CALLS,
    configurationKeys: [{ key: 'SiteName', label: 'Site Name' }],
    modalTitle: 'Video Calling configuration',
  },
  [Feature.KIOSK_AUDIO_CALLS]: {
    featureValue: Feature.KIOSK_AUDIO_CALLS,
    configurationKeys: [{ key: 'SiteName', label: 'Site Name' }],
    modalTitle: 'Kiosk Audio Calls configuration',
  },
  [Feature.AUDIO_VISITATION]: {
    featureValue: Feature.AUDIO_VISITATION,
    configurationKeys: [{ key: 'SiteName', label: 'Site Name' }],
    modalTitle: 'Audio Visitation configuration',
  },

  // Visitations: Requires ConnectServerIp, ConnectServerPort, SiteName
  [Feature.VIDEO_VISITATION]: {
    featureValue: Feature.VIDEO_VISITATION,
    configurationKeys: [
      { key: 'ConnectServerIp', label: 'Connect Server IP' },
      { key: 'ConnectServerPort', label: 'Connect Server Port' },
      { key: 'SiteName', label: 'Site Name' }
    ],
    modalTitle: 'Video Visitation configuration',
  },

  // Law library: Requires LawLibraryUrl
  [Feature.LAW_LIBRARY]: {
    featureValue: Feature.LAW_LIBRARY,
    configurationKeys: [{ key: 'LawLibraryUrl', label: 'Law Library URL' }],
    modalTitle: 'Law Library configuration',
  },

  // Education: Requires EducationUrl
  [Feature.EDUCATION]: {
    featureValue: Feature.EDUCATION,
    configurationKeys: [{ key: 'EducationUrl', label: 'Education URL' }],
    modalTitle: 'Education configuration',
  },

  // Inmate Face Login: Optional frequency (defaults to always)
  [Feature.INMATE_FACE_LOGIN]: {
    featureValue: Feature.INMATE_FACE_LOGIN,
    configurationKeys: [{
      key: 'FaceLoginFrequencyMinutes',
      label: 'Face login frequency',
      defaultValue: '0',
      options: [
        { value: '0', label: 'Always' },
        { value: '15', label: 'Every 15 minutes' },
        { value: '30', label: 'Every 30 minutes' },
        { value: '60', label: 'Every 1 hour' },
        { value: '120', label: 'Every 2 hours' },
        { value: '240', label: 'Every 4 hours' },
        { value: '480', label: 'Every 8 hours' },
        { value: '1440', label: 'Every 24 hours' },
      ],
    }],
    modalTitle: 'Face Login configuration',
  },
};

function getFeatureActivationRequirements(
  facility?: FeatureActivationFacility | null
): Partial<Record<string, FeatureActivationRequirement>> {
  return {
    ...STATIC_FEATURE_ACTIVATION_REQUIREMENTS,

    // Booking call logic will vary by facility state as MN requires 10min booking calls
    [Feature.BOOKING_CALLS]: {
      featureValue: Feature.BOOKING_CALLS,
      configurationKeys: [{
        key: 'BookingCallMinutes',
        label: 'Booking call time',
        defaultValue: bookingCallMinutesDefault(facility),
        // Minnesota facilities are required to offer at least 10 minutes of booking call time
        options: isMinnesotaFacility(facility)
          ? [{ value: '10', label: '10 minutes [MN requirement]' }]
          : BOOKING_CALL_MINUTES_OPTIONS,
      }],
      modalTitle: 'Booking Calls configuration',
    },
  };
}

function configurationValueForKey(
  key: string,
  configurations: Configuration[],
  pending: Record<string, string> | undefined,
  defaultValue?: string
): string {
  const row = configurations.find((configuration) => configuration.key === key);
  const serverValue = row?.value ?? '';
  if (String(serverValue).trim()) {
    return serverValue;
  }

  // If the value is pending, return that, otherwise return the default value
  if (pending?.[key] != null && String(pending[key]).trim()) {
    return pending[key];
  }
  return defaultValue ?? '';
}

/** True when every required key has a non-empty value (server, pending, or default). Keys are shared across features. */
export function configsMeetRequirement(
  requirement: FeatureActivationRequirement,
  configurations: Configuration[],
  pending: Record<string, string> | undefined
): boolean {
  for (const spec of requirement.configurationKeys) {
    const raw = configurationValueForKey(spec.key, configurations, pending, spec.defaultValue);
    if (!String(raw).trim()) {
      return false;
    }
  }
  return true;
}

/** True when every key has a saved or pending value (ignores the default value). */
export function configsAreExplicitlySet(
  requirement: FeatureActivationRequirement,
  configurations: Configuration[],
  pending: Record<string, string> | undefined
): boolean {
  for (const spec of requirement.configurationKeys) {
    const raw = configurationValueForKey(spec.key, configurations, pending);
    if (!String(raw).trim()) {
      return false;
    }
  }
  return true;
}

export function validateGatedFeaturesForSubmit(
  values: {
    features?: unknown;
    pendingFacilityConfigurations?: Record<string, string>;
    state?: string;
  },
  configurations: Configuration[]
): string | undefined {
  const featureList = getFacilityFeatureList(values.features);
  const requirements = getFeatureActivationRequirements({ state: values.state ?? '' });
  for (const requirement of Object.values(requirements)) {
    if (requirement == null) {
      continue;
    }
    if (!featureList.includes(requirement.featureValue)) {
      continue;
    }
    const satisfied = configsMeetRequirement(
      requirement,
      configurations,
      values.pendingFacilityConfigurations
    );
    if (!satisfied) {
      return 'One or more features need configuration. Use the prompts on the Features card before saving.';
    }
  }
  return undefined;
}

type ModalContext = {
  featureValue: string;
  intent: 'enable' | 'fix';
};

type FeatureCheckboxesProps = {
  form: FormRenderProps;
  facility?: Facility;
};

const FeatureCheckboxes: React.FC<FeatureCheckboxesProps> = (props) => {
  const { form, facility } = props;
  const { values, form: formApi } = form;

  const configurationsQuery = useFacilityConfigurations(facility?.id);
  const configurations = configurationsQuery.result ?? [];
  const configurationsLoading = configurationsQuery.loading && facility != null;

  const [modalContext, setModalContext] = useState<ModalContext | null>(null);

  const facilityForRequirements = useMemo(
    (): FeatureActivationFacility => ({
      state: (values.state as string | undefined) ?? facility?.state ?? '',
    }),
    [facility?.state, values.state]
  );
  const featureActivationRequirements = useMemo(
    () => getFeatureActivationRequirements(facilityForRequirements),
    [facilityForRequirements]
  );

  const activeRequirement = useMemo(() => {
    if (modalContext == null) {
      return null;
    }
    return featureActivationRequirements[modalContext.featureValue] ?? null;
  }, [modalContext, featureActivationRequirements]);

  const pendingFacilityConfigurations: Record<string, string> = values.pendingFacilityConfigurations ?? {};

  const handleConfigurationModalClose = (saved?: boolean) => {
    const context = modalContext;
    setModalContext(null);
    if (!saved || context == null) {
      return;
    }
    if (facility?.id != null) {
      void configurationsQuery.execute(facility.id);
    }
    if (context.intent === 'enable') {
      const current = getFacilityFeatureList(values.features);
      if (!current.includes(context.featureValue)) {
        formApi.change('features', [...current, context.featureValue]);
      }
    }
  };

  const handleConfigurationSave = async (values: Record<string, string>) => {
    if (activeRequirement == null) {
      return undefined;
    }

    // If we don't have a facility yet (add facility flow), save the values to the pending facility configurations
    // The configurations will be saved when the facility is created
    if (facility?.id == null) {
      formApi.change('pendingFacilityConfigurations', {
        ...pendingFacilityConfigurations,
        ...values,
      });
      return undefined;
    }

    // If we have a facility (edit facility flow), save the values to the facility configurations
    const errorResult = await saveFacilityConfigurations(
      facility.id,
      activeRequirement.configurationKeys,
      configurations,
      values
    );

    if (errorResult != null) {
      return errorResult;
    }

    return undefined;
  };

  return (
    <>
      <Card>
        <Card.Header>Features at Facility</Card.Header>
        <Card.Body>
          {Object.keys(Feature)
            .sort()
            .map((featureEnumKey) => {
              const featureLabel = Feature[featureEnumKey as keyof typeof Feature];
              const requirement = featureActivationRequirements[featureLabel];

              if (requirement == null) {
                return (
                  <Checkbox
                    key={`facility-add-feature-${featureEnumKey}`}
                    id={`facility-feature-${featureEnumKey}`}
                    name="features"
                    type="switch"
                    value={featureLabel}
                    label={featureLabel}
                  />
                );
              }

              const enabled = getFacilityFeatureList(values.features).includes(requirement.featureValue);
              const satisfied = configsMeetRequirement(
                requirement,
                configurations,
                pendingFacilityConfigurations
              );
              const showViolation = enabled && !configurationsLoading && !satisfied;

              return (
                <Field key={`facility-gated-feature-${featureEnumKey}`} name="features" type="checkbox" value={requirement.featureValue}>
                  {({ input }) => (
                    <FormGroup
                      className={showViolation ? 'border border-danger rounded px-2 py-1 mb-2' : 'mb-2'}
                      onClick={
                        showViolation
                          ? (event) => {
                              const target = event.target as HTMLElement;
                              if (target.closest('input, label, .form-check')) {
                                return;
                              }
                              setModalContext({ featureValue: requirement.featureValue, intent: 'fix' });
                            }
                          : undefined
                      }
                      style={showViolation ? { cursor: 'pointer' } : undefined}>
                      <Form.Check
                        {...input}
                        checked={enabled}
                        disabled={configurationsLoading}
                        id={`facility-feature-${featureEnumKey}`}
                        label={featureLabel}
                        onChange={(event) => {
                          if (!event.target.checked) {
                            input.onChange(event);
                            return;
                          }
                          if (
                            configsAreExplicitlySet(
                              requirement,
                              configurations,
                              pendingFacilityConfigurations
                            )
                          ) {
                            input.onChange(event);
                            return;
                          }
                          event.preventDefault();
                          setModalContext({ featureValue: requirement.featureValue, intent: 'enable' });
                        }}
                        type="switch"
                      />
                      {showViolation && (
                        <div className="text-danger small mt-1">
                          Configuration missing or incomplete.
                          <button
                            className="btn btn-link btn-sm text-danger p-0 ms-1"
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              setModalContext({ featureValue: requirement.featureValue, intent: 'fix' });
                            }}>
                            Fix
                          </button>
                        </div>
                      )}
                    </FormGroup>
                  )}
                </Field>
              );
            })}
        </Card.Body>
      </Card>

      {activeRequirement != null && (
        <FeatureConfigurationModal
          show={modalContext != null}
          title={activeRequirement.modalTitle}
          configurationKeys={activeRequirement.configurationKeys}
          configurations={configurations}
          pendingValues={pendingFacilityConfigurations}
          onSave={handleConfigurationSave}
          onClose={handleConfigurationModalClose}
        />
      )}
    </>
  );
};

export default FeatureCheckboxes;
